import { Component, HostListener } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest, map, Observable, shareReplay, startWith } from 'rxjs';
import { GeneralDataRepository } from 'app/helpers/repository/general-data.repository';
import { NonFeatureMenuItems, TenantFeatures } from 'app/state/feature.repository';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IconName } from 'app/shared/igntr-icons';
import { LocalStorageService } from 'app/api/services/local-storage-service';
import { AuthService } from 'app/state/auth.service';
import { getStoredToken } from 'app/state/auth.repository';
import { DatalistOption } from 'app/state/abstract/base.repository';
import { MessageText, ToastService } from 'app/helpers/services/toastService';
import { SalesProfileService } from 'app/api/services/sales-profile.service';

interface MobileTab {
  path: string;
  label: string;
  /** Transloco key for the visible label — used as the fallback when the
   *  tenant hasn't set a custom title for this feature. */
  labelKey: string;
  icon: IconName;
  /** Tenant feature this tab depends on. `null` means always render. */
  feature: TenantFeatures | null;
  /** Name used to look this tab up in `tenantMenuItemsData` so the tab can
   *  show the tenant's custom title (e.g. "Værktøjer", "Mine forløb") — the
   *  same source the home grid uses. `null` = no tenant override available. */
  menuKey: string | null;
  /** Resolved per-tenant custom title (populated at runtime). When set, it
   *  takes priority over `labelKey` in the template. */
  displayName?: string;
}

/** Per-feature icon map for items rendered inside the Mere bottom sheet.
 *  Keys are sidebar item `title` (lower-cased upstream). Falls back to
 *  `grid` for unknown items so nothing breaks when a new feature appears. */
const SHEET_ICONS: Record<string, IconName> = {
  home: 'activity',
  feed: 'users',
  sprints: 'bookmark',
  toolbox: 'grid',
  dataroom: 'layers',
  news: 'message',
  personalplan: 'target',
  coach: 'user',
  airoleplay: 'drama',
  notifications: 'bell',
  playbook: 'sparkle',
  playbookpilot: 'sparkle',
  aiselling: 'sparkle',
  todo: 'check',
  chat: 'message',
  calendar: 'calendar',
  timelogs: 'clock',
  whistleblow: 'shield',
  messages: 'mail',
  outofofficestatus: 'calendar',
  onboarding: 'rocket',
  absent: 'pen',
  absentmanager: 'pen',
  poll: 'poll',
  pagebuilder: 'doc',
  employee: 'user',
  canvas: 'kanban',
  phonebook: 'phone',
  crm: 'briefcase',
  currency: 'tag',
  product: 'database',
  'sds links/cas': 'database',
  kemihotelletapv: 'flask',
  tenants: 'building',
  users: 'user',
  usergroup: 'users',
  email: 'mail',
  reports: 'chart',
  dashboard: 'grid',
  activities: 'activity',
  documents: 'doc',
  timelogsoverview: 'clock',
};

interface SheetItem {
  title: string;
  displayedName: string;
  routerLink: string;
  icon: IconName;
  notificationsCount: number;
}

interface SheetSection {
  key: 'main' | 'ignite' | 'admin';
  label: string;
  badge?: string;
  items: SheetItem[];
}

@UntilDestroy()
@Component({
  selector: 'app-navbar',
  standalone: false,
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent {
  currentRoute = '';
  moreOpen = false;

  /** Fixed primary tabs — labels match the handoff template. Feature flag
   *  hides a tab if the tenant doesn't have it enabled. */
  private readonly tabsTemplate: readonly MobileTab[] = [
    { path: '/home',                  label: 'Home',    labelKey: 'home',    icon: 'home',    feature: null,                        menuKey: NonFeatureMenuItems.Home },
    { path: '/sprints',               label: 'Sprints', labelKey: 'sprints', icon: 'flame',   feature: TenantFeatures.Sprints,      menuKey: TenantFeatures.Sprints },
    { path: '/toolbox/areas',         label: 'Tools',   labelKey: 'tools',   icon: 'toolbox', feature: TenantFeatures.ToolBox,      menuKey: TenantFeatures.ToolBox },
    { path: '/personalplan',          label: 'Plan',    labelKey: 'plan',    icon: 'check',   feature: TenantFeatures.PersonalPlan, menuKey: TenantFeatures.PersonalPlan },
  ];

  /** Visible tabs after feature-flag filtering. */
  mobileTabs: MobileTab[] = [];

  /** Sections shown in the Mere bottom sheet — sourced from the same
   *  per-tenant data the desktop sidebar uses. Empty sections are dropped. */
  sections$!: Observable<SheetSection[]>;

  /** Unread count for the Inbox tab badge. */
  inboxBadge$!: Observable<number>;

  /** Label of the currently active tenant — shown in the Mere sheet's
   *  "IGNTR · {tenant}" header tagline. Resolved from tenantsOptions$ +
   *  the active tenant id in local storage (same wiring as the sidebar). */
  activeTenantLabel$!: Observable<string>;

  // ---------------------------------------------------------------------------
  // Tenant switcher (Mere sheet) — parallel state to the desktop sidebar's
  // tenant chip. Only rendered when the user has access to >1 tenant.
  // ---------------------------------------------------------------------------
  /** All tenants the user can switch to (sorted by label). */
  tenantsOptions: Array<DatalistOption> = [];
  /** Active tenant id, mirrors localStorage('activeTenantId'). */
  tenantId: string | null = null;
  /** Label of the active tenant — for the tile in the sheet. */
  activeTenantLabel = '';
  /** Whether the in-sheet tenant dropdown is expanded. */
  tenantPickerOpen = false;

  private hasQuestionnaireFeature = false;
  private isAnyAdmin = false;
  // null = not yet known (don't lock before the first /me resolves).
  private questionnaireCompleted: boolean | null = null;
  readonly lockedTooltip = 'Besvar spørgsmålene for at fortsætte.';

  /** Regular user on a Questionnaire tenant who hasn't completed it yet. */
  get locked(): boolean {
    return (
      this.hasQuestionnaireFeature &&
      !this.isAnyAdmin &&
      this.questionnaireCompleted === false
    );
  }

  /** Home is always reachable so the user can get back to the questionnaire. */
  isHomeTab(tab: MobileTab): boolean {
    return (
      tab.path === '/home' ||
      tab.path?.toLowerCase() === '/home' ||
      tab.label?.toLowerCase() === 'home'
    );
  }

  /** Same Home exemption for the overflow-sheet rows. */
  isHomeRow(item: SheetItem): boolean {
    const link = (item?.routerLink || '').toLowerCase();
    return (
      link === '/home' ||
      link.startsWith('/home') ||
      item?.displayedName?.toLowerCase() === 'home' ||
      item?.title?.toLowerCase() === 'home'
    );
  }

  /** First letter of the active tenant — used by the avatar tile that sits
   *  to the left of the tenant name. */
  get activeTenantInitial(): string {
    return (this.activeTenantLabel || '?').charAt(0).toUpperCase();
  }

  /** Deterministic gradient for the tenant avatar — same hash approach the
   *  desktop sidebar uses so the same tenant gets the same color everywhere. */
  avatarBgFor(label: string): string {
    let hash = 0;
    for (let i = 0; i < label.length; i++) {
      hash = (hash * 31 + label.charCodeAt(i)) & 0xffffffff;
    }
    const hue = Math.abs(hash) % 360;
    return `linear-gradient(135deg, oklch(0.62 0.15 ${hue}), oklch(0.48 0.18 ${hue}))`;
  }

  /** Convenience for the active tenant's avatar gradient. */
  get activeTenantAvatarBg(): string {
    return this.avatarBgFor(this.activeTenantLabel);
  }

  constructor(
    public generalDataRepo: GeneralDataRepository,
    public router: Router,
    private storage: LocalStorageService,
    private auth: AuthService,
    private toastService: ToastService,
    private salesProfileService: SalesProfileService,
  ) {
    this.router.events.pipe(untilDestroyed(this)).subscribe((x) => {
      if (x instanceof NavigationEnd) {
        this.currentRoute = x.url;
        if (this.moreOpen) this.moreOpen = false;
      }
    });
  }

  ngOnInit() {
    this.generalDataRepo.allGeneralData$
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        const features: TenantFeatures[] = data?.features ?? [];
        const menuData = data?.tenantMenuItemsData ?? [];
        this.hasQuestionnaireFeature =
          features.includes(TenantFeatures.Questionnaire) &&
          features.includes(TenantFeatures.PersonalPlan);
        this.isAnyAdmin = !!data?.activeUserData?.isAnyAdmin;
        this.mobileTabs = this.tabsTemplate
          .filter((t) => t.feature === null || features.includes(t.feature))
          .map((t) => {
            // Resolve the tenant's custom title for this tab (same lookup the
            // home grid uses) so renamed features stay consistent everywhere.
            const featureData = t.menuKey
              ? menuData.find(
                  (d) => d.name?.toLowerCase() === t.menuKey!.toLowerCase(),
                )
              : undefined;
            return { ...t, displayName: featureData?.customTitle || undefined };
          });
      });

    this.sections$ = combineLatest([
      this.generalDataRepo.allMainSideMenuItems$.pipe(startWith([] as any[])),
      this.generalDataRepo.allIgniteSideMenuItems$.pipe(startWith([] as any[])),
      this.generalDataRepo.allAdminSideMenuItems$.pipe(startWith([] as any[])),
    ]).pipe(
      map(([main, ignite, admin]) => {
        // The Inbox primary tab was removed, so surface the Notifications row
        // in the More sheet to keep it reachable.
        const sections: SheetSection[] = [
          { key: 'main',   label: 'Main',        items: (main ?? []).map((i) => this.toSheetItem(i)) },
          { key: 'ignite', label: 'Ignite',      items: (ignite ?? []).map((i) => this.toSheetItem(i)), badge: 'AI' },
          { key: 'admin',  label: 'Admin Panel', items: (admin  ?? []).map((i) => this.toSheetItem(i)) },
        ];
        return sections.filter((s) => s.items.length);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.inboxBadge$ = this.generalDataRepo.allMainSideMenuItems$.pipe(
      startWith([] as any[]),
      map((items: any[]) => {
        const inbox = (items ?? []).find(
          (i) => i?.title?.toLowerCase?.() === 'notifications',
        );
        return Number(inbox?.notificationsCount) || 0;
      }),
    );

    this.activeTenantLabel$ = combineLatest([
      this.generalDataRepo.tenantsOptions$,
      this.storage.getTenantId$(),
    ]).pipe(
      map(([options, id]) => {
        const match = (options ?? []).find((o: any) => o?.value === id);
        return match?.label ?? '';
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    // Mirror the desktop sidebar's tenant-switcher state — sorted options +
    // resolved active label kept in component fields so the template can
    // render them without async pipes on every cell.
    combineLatest([
      this.generalDataRepo.tenantsOptions$,
      this.storage.getTenantId$(),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([options, id]) => {
        this.tenantsOptions = [...(options ?? [])].sort((a, b) =>
          a.label.localeCompare(b.label),
        );
        this.tenantId = id;
        const match = this.tenantsOptions.find((o) => o.value === id);
        this.activeTenantLabel = match?.label ?? '';
      });

    // Real-time questionnaire lock (matches the sidebar/breadcrumb): finishing
    // or resetting the questionnaire pushes into completed$.
    this.salesProfileService.completed$
      .pipe(untilDestroyed(this))
      .subscribe((completed) => (this.questionnaireCompleted = completed));
  }

  /** Switch the active tenant. Same wiring as the desktop sidebar's
   *  `setActiveTenant` — writes the id to local storage, posts to the
   *  auth endpoint, refreshes general data, and navigates to /home so the
   *  user lands somewhere safe regardless of which page they were on. */
  setActiveTenant(tenantId: string | null): void {
    const token = getStoredToken();
    if (!token) {
      console.error('Cannot switch tenant: no auth token available');
      return;
    }
    // Switch server-side first; only mirror into client state on success so a
    // failed request can't leave the UI on a tenant the backend still isn't
    // scoped to (which surfaces the previous tenant's data).
    this.auth
      .setActiveTenant(token, tenantId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.tenantId = tenantId;
          if (tenantId) {
            localStorage.setItem('activeTenantId', tenantId);
          } else {
            localStorage.removeItem('activeTenantId');
          }
          this.storage.setTenantId(tenantId);
          this.generalDataRepo.updateAllGeneralData(true);
          this.tenantPickerOpen = false;
          this.closeMore();
          this.router.navigate(['/home']);
        },
        error: (err) => {
          console.error('Failed to switch tenant', err);
          this.toastService.showError(MessageText.switchTenantFailed);
        },
      });
  }

  /** Toggle the in-sheet tenant dropdown. */
  toggleTenantPicker(): void {
    this.tenantPickerOpen = !this.tenantPickerOpen;
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.moreOpen) this.moreOpen = false;
  }

  closeMore(): void {
    this.moreOpen = false;
    this.tenantPickerOpen = false;
  }

  openMore(): void {
    this.moreOpen = true;
  }

  /** URL-prefix match — same shape as the desktop sidebar's active-row
   *  logic. The home route also matches the empty `/` root. */
  checkUrl(url: string): boolean {
    if (!url) return false;
    const normalized = url.startsWith('/') ? url : '/' + url;
    if (
      (normalized === '/home') &&
      (this.currentRoute === '/' || this.currentRoute === '/home' || this.currentRoute.startsWith('/home'))
    ) {
      return true;
    }
    return (
      this.currentRoute === normalized ||
      this.currentRoute.startsWith(normalized + '/')
    );
  }

  /** True when no primary tab matches the current URL — highlights the Mere
   *  button on routes like Data Room, CRM, Roleplay, Tenants. Also stays
   *  highlighted while the sheet is open. */
  isMoreActive(): boolean {
    if (this.moreOpen) return true;
    return !this.mobileTabs.some((t) => this.checkUrl(t.path));
  }

  /** Convert a raw SideBarItem from the repository into the shape the
   *  Mere sheet expects. Icon is mapped via SHEET_ICONS. */
  private toSheetItem(raw: any): SheetItem {
    const titleKey = (raw?.title || '').toString().toLowerCase();
    return {
      title: titleKey,
      displayedName: raw?.displayedName || raw?.title || '',
      routerLink: raw?.routerLink || '',
      icon: SHEET_ICONS[titleKey] ?? 'grid',
      notificationsCount: Number(raw?.notificationsCount) || 0,
    };
  }
}
